import type { SalesPostingSettings } from './commission.js';
import type { AvatarPresetId } from './membership.js';
/**
 * CRM/Sales type mirrors — hand-mirrored from apps/api/src/crm/**.controller.ts
 * response shapes (apps/client is a browser bundle, cannot import the
 * Node-composite apps/api or packages/modules/crm-sales directly).
 */

export interface PipelineStage {
  readonly ref: string;
  readonly name: string;
  readonly sortOrder: number;
  readonly probabilityLabel: string;
  readonly leadCount: number;
}

export interface PipelineResponse {
  readonly pipelineName: string;
  readonly stages: readonly PipelineStage[];
}

export interface RecentActivity {
  readonly ref: string;
  readonly title: string;
  readonly activityTypeLabel: string;
  readonly statusLabel: string;
  readonly dueAt: string | null;
}

export interface RecentActivitiesResponse {
  readonly activities: readonly RecentActivity[];
}

export interface SalesAction {
  readonly label: string;
  readonly availability: 'available' | 'blocked';
  readonly blockedReason: string | null;
}

export interface SalesActionsResponse {
  readonly actions: readonly SalesAction[];
}

/** `GET .../crm/reports/pipeline` — apps/api/src/crm/reports.controller.ts `pipeline()`. Real Rial amounts (server-side BigInt sum), not the count-only `GET .../crm/pipeline` above. */
export interface PipelineStageReport {
  readonly ref: string;
  readonly name: string;
  readonly sortOrder: number;
  readonly probabilityPct: number;
  readonly leadCount: number;
  readonly amountRial: string;
  readonly weightedAmountRial?: string | undefined;
}

export interface PipelineReportResponse {
  readonly pipelineName: string;
  readonly authority: 'operational';
  readonly stages: readonly PipelineStageReport[];
  readonly totals: {
    readonly leadCount: number;
    readonly amountRial: string;
    readonly weightedAmountRial?: string | undefined;
  };
}

/** `GET .../crm/reports/customers` — apps/api/src/crm/reports.controller.ts
 * `customers()`, packages/modules/crm-sales/src/reports.ts
 * `aggregateCustomerSalesReport`. All-time, top 50 by total, cancelled
 * orders excluded — the query has no date-range filter yet, so this is not
 * "this period's sales", it is "sales to date"; print templates must say so
 * rather than implying a period the backend cannot actually scope. */
export interface CustomerSalesReportRow {
  readonly partyRef: string;
  readonly partyName: string | null;
  readonly orderCount: number;
  readonly totalRial: string;
}

export interface CustomerSalesReportResponse {
  readonly authority: 'operational';
  readonly customers: readonly CustomerSalesReportRow[];
}

/** `GET .../crm/reports/sales-representatives` — exact server-side totals.
 * Confirmed, partially fulfilled and fulfilled orders are included; draft and
 * cancelled orders are excluded. Pagination is over representatives, not raw orders. */
export interface SalesRepresentativePerformanceRow {
  readonly rank: number;
  readonly representativeName: string;
  readonly isCurrentUser: boolean;
  readonly orderCount: number;
  readonly totalRial: string;
}

export interface SalesRepresentativePerformanceResponse {
  readonly authority: 'operational';
  readonly dateBasis: 'created_at';
  readonly includedOrderStatuses: readonly ['confirmed', 'partially_fulfilled', 'fulfilled'];
  readonly fromDate: string | null;
  readonly toDate: string | null;
  readonly offset: number;
  readonly limit: number;
  readonly total: number;
  readonly rows: readonly SalesRepresentativePerformanceRow[];
  readonly totals: {
    readonly representativeCount: number;
    readonly orderCount: number;
    readonly totalRial: string;
  };
}

/** `GET .../crm/reports/overdue-activities` — apps/api/src/crm/reports.controller.ts `overdueActivities()`. */
export interface OverdueActivityItem {
  readonly ref: string;
  readonly title: string;
  readonly typeLabel: string;
  readonly statusLabel: string;
  readonly dueAt: string | null;
}

export interface OverdueActivityReportResponse {
  readonly authority: 'operational';
  readonly count: number;
  readonly activities: readonly OverdueActivityItem[];
}

export interface Lead {
  readonly ref: string;
  readonly title: string;
  readonly sourceLabel: string | null;
  readonly statusLabel: string;
  readonly createdAt: string;
}

export type LeadKind = 'person' | 'organization';
export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'disqualified';

export interface CrmMember {
  /** Signed, tenant-scoped; null for an inactive past owner (not reassignable). */
  readonly memberRef: string | null;
  readonly name: string;
  readonly avatarPreset: AvatarPresetId | null;
  readonly active: boolean;
}

export type LeadPriority = 'low' | 'normal' | 'high' | 'urgent';

/**
 * The sales-deal side of a lead (what used to be a separate «فرصت فروش»):
 * stage on the pipeline, amount, expected close, priority and the lead's own
 * next step. `GET .../crm/leads` presents it as `deal`.
 */
export interface LeadDeal {
  readonly customer: { readonly ref: string; readonly label: string } | null;
  readonly pipeline: { readonly ref: string; readonly name: string } | null;
  readonly stage: { readonly ref: string; readonly name: string } | null;
  readonly probabilityPct: number;
  readonly amountRial: string;
  readonly weightedAmountRial: string;
  readonly expectedCloseDate: string | null;
  readonly priority: LeadPriority;
  readonly priorityLabel: string;
  readonly needDescription: string | null;
  readonly ownNextAction: { readonly title: string; readonly dueAt: string | null } | null;
  readonly lostReason: {
    readonly ref: string;
    readonly label: string;
    readonly note: string | null;
  } | null;
  readonly wonCustomerRef: string | null;
  readonly closedAt: string | null;
  readonly stageEnteredAt: string | null;
  readonly updatedAt: string;
}

/** `GET .../crm/leads` row — contact numbers are always masked here. */
export interface LeadRow extends Lead {
  readonly labels?: readonly import('./customers.js').CrmLabel[];
  readonly displayName: string;
  readonly leadKind: LeadKind | null;
  readonly leadKindLabel: string | null;
  readonly needSubject: string | null;
  readonly sourceDetail: string | null;
  readonly status: LeadStatus;
  readonly disqualifyReason: string | null;
  readonly deal: LeadDeal;
  readonly contact: {
    readonly mobileMasked: string | null;
    readonly phoneMasked: string | null;
    readonly altContactLabel: string | null;
    readonly hasContact: boolean;
  };
  readonly owner: CrmMember | null;
  readonly isMine: boolean;
  readonly lastActivity: {
    readonly title: string;
    readonly typeLabel: string;
    readonly at: string;
  } | null;
  readonly nextAction: {
    readonly title: string;
    readonly typeLabel: string;
    readonly dueAt: string | null;
  } | null;
}

export interface LeadListResponse {
  readonly leads: readonly LeadRow[];
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly sourceOptions: readonly string[];
}

export interface LeadStageOption {
  readonly ref: string;
  readonly name: string;
  readonly sortOrder: number;
  readonly probabilityPct: number;
}

export interface LeadBoardStage extends LeadStageOption {
  readonly count: number;
  readonly amountRial: string;
  readonly weightedAmountRial: string;
  readonly hasMore: boolean;
  readonly leads: readonly LeadRow[];
}

/** `GET .../crm/leads/board` — open leads grouped by pipeline stage. */
export interface LeadBoardResponse {
  readonly pipelineName: string;
  readonly stages: readonly LeadBoardStage[];
}

export interface LeadDetail extends LeadRow {
  readonly personFirstName: string | null;
  readonly personLastName: string | null;
  readonly organizationName: string | null;
  readonly products: readonly { readonly ref: string; readonly name: string }[];
  readonly stageTimeline: readonly {
    readonly previousStageRef: string | null;
    readonly previousStageName: string | null;
    readonly nextStageRef: string | null;
    readonly nextStageName: string | null;
    readonly actor: CrmMember | null;
    readonly transitionedAt: string;
  }[];
  /** Stages ahead of the current one — the only moves the API accepts. */
  readonly allowedStages: readonly LeadStageOption[];
  readonly lostReasons: readonly { readonly ref: string; readonly name: string }[];
  readonly timeline: readonly {
    readonly kind: 'activity' | 'note';
    readonly ref: string;
    readonly title: string;
    readonly typeLabel: string;
    readonly statusLabel: string | null;
    readonly at: string;
    readonly dueAt: string | null;
    readonly by: string | null;
  }[];
  readonly actions: {
    readonly canUpdate: boolean;
    readonly canMoveStage: boolean;
    readonly canWin: boolean;
    readonly canLose: boolean;
    readonly canCreateCustomer: boolean;
    readonly canQuote: boolean;
    readonly canLogActivity: boolean;
  };
}

export interface LeadOptionsResponse {
  readonly sources: readonly string[];
  readonly sourcesRequiringDetail: readonly string[];
  readonly pipelines: readonly {
    readonly ref: string;
    readonly name: string;
    readonly isDefault: boolean;
    readonly stages: readonly LeadStageOption[];
  }[];
  readonly lostReasons: readonly { readonly ref: string; readonly name: string }[];
  readonly priorities: readonly { readonly value: LeadPriority; readonly label: string }[];
  readonly owners: readonly (CrmMember & { readonly isMe: boolean })[];
  readonly canAssignOwner?: boolean;
}

export interface LeadOwnersResponse {
  readonly owners: readonly {
    readonly memberRef: string;
    readonly displayLabel: string;
  }[];
}

export interface LeadContactReveal {
  readonly mobile: string | null;
  readonly phone: string | null;
  readonly altContactLabel: string | null;
  readonly altContactValue: string | null;
}

export interface Activity {
  readonly ref: string;
  readonly title: string;
  readonly activityTypeLabel: string;
  readonly statusLabel: string;
  readonly leadRef: string | null;
  readonly partyRef: string | null;
  readonly dueAt: string | null;
  readonly createdAt: string;
}

export interface ActivityListResponse {
  readonly activities: readonly Activity[];
}

/** A resolved quote/sales-order line — `GET .../sales/{quotes,orders}/:ref`'s
 * `lines` field, apps/api/src/crm/sales-workflow.controller.ts `readQuote`/
 * `readOrder`, mirroring `SalesDocumentLineView` from
 * packages/modules/crm-sales/src/sales-lines.ts (BUG-07: discount/tax
 * added — `lineTotalRial` is tax-inclusive, the one authoritative amount). */
export interface SalesDocumentLine {
  readonly lineId: string;
  readonly rowNumber: number;
  readonly productRef: string;
  readonly description: string;
  readonly quantity: string;
  readonly uomRef: string;
  readonly unitPriceRial: string;
  readonly discountRial: string;
  readonly taxRef: string | null;
  readonly taxAmountRial: string;
  readonly lineTotalRial: string;
}

export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
export type QuoteTransitionTarget = 'sent' | 'accepted' | 'rejected';

export interface Quote {
  readonly ref: string;
  readonly status: QuoteStatus;
  readonly statusLabel: string;
  readonly allowedTransitions: readonly QuoteTransitionTarget[];
  readonly totalRial: string;
  readonly validUntil: string | null;
  readonly partyRef: string | null;
  readonly leadRef: string | null;
  readonly lines?: readonly SalesDocumentLine[];
}

export interface QuoteListResponse {
  readonly quotes: readonly Quote[];
}

export interface SalesOrder {
  readonly ref: string;
  readonly status: 'draft' | 'confirmed' | 'partially_fulfilled' | 'fulfilled' | 'cancelled';
  readonly statusLabel: string;
  readonly totalRial: string;
  readonly quoteRef: string | null;
  readonly partyRef: string | null;
  readonly lines?: readonly SalesDocumentLine[];
  readonly fulfillment?: SalesOrderFulfillment;
  readonly actions?: {
    readonly canConfirm: boolean;
    readonly canCancel: boolean;
    readonly canDeliver: boolean;
  };
}

export interface SalesOrderListResponse {
  readonly orders: readonly SalesOrder[];
}

export type SalesInvoiceStatus = 'draft' | 'submitted' | 'approved' | 'posted' | 'reversed';

export interface SalesInvoice {
  readonly invoiceId: string;
  readonly orderRef: string;
  readonly partyId: string;
  readonly partyRef: string;
  readonly partyName: string;
  readonly status: SalesInvoiceStatus;
  readonly businessDate: string;
  readonly dueDate: string;
  readonly paymentTerm: { readonly code: string; readonly name: string; readonly dueDays: number };
  readonly currencyCode: string;
  readonly grossAmountRial: string;
  readonly discountAmountRial: string;
  readonly discountApprovalRequired: boolean;
  readonly taxableBaseRial: string;
  readonly vatAmountRial: string;
  readonly totalAmountRial: string;
  readonly taxSnapshot: null | {
    readonly taxId: string;
    readonly code: string;
    readonly name: string;
    readonly version: number;
    readonly rateNumerator: string;
    readonly rateDenominator: string;
  };
  readonly arOpenItemId: string;
  readonly receivableStatus: string;
  readonly openBalanceRial: string;
  readonly revenueJournalId: string;
  readonly officialNumber: string | null;
  readonly reversalJournalId: string | null;
  readonly lines: readonly {
    readonly invoiceLineId: string;
    readonly orderLineId: string;
    readonly rowNumber: number;
    readonly productRef: string;
    readonly description: string;
    readonly quantity: string;
    readonly uomRef: string;
    readonly unitPriceRial: string;
    readonly grossAmountRial: string;
    readonly discountAmountRial: string;
    readonly discountReason: string | null;
    readonly taxableBaseRial: string;
    readonly vatAmountRial: string;
    readonly totalAmountRial: string;
    readonly allocations: readonly { readonly movementId: string; readonly quantity: string }[];
  }[];
}

export interface SalesInvoiceListResponse {
  readonly invoices: readonly SalesInvoice[];
}

export interface SalesInvoiceListRow {
  readonly invoiceId: string;
  readonly orderRef: string;
  readonly partyRef: string;
  readonly partyName: string;
  readonly status: SalesInvoiceStatus;
  readonly businessDate: string;
  readonly dueDate: string;
  readonly totalAmountRial: string;
  readonly officialNumber: string | null;
}

export interface SalesInvoiceGlobalListResponse {
  readonly invoices: readonly SalesInvoiceListRow[];
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
}

export type FulfillmentStatus =
  | 'unreserved'
  | 'partially_reserved'
  | 'ready_to_deliver'
  | 'partially_delivered'
  | 'fully_delivered';

export interface SalesOrderFulfillmentLine {
  readonly lineId: string;
  readonly rowNumber: number;
  readonly productRef: string;
  readonly productName: string;
  readonly uomRef: string;
  readonly uomName: string;
  readonly orderedQuantity: string;
  readonly reservedQuantity: string;
  readonly deliveredQuantity: string;
  readonly remainingQuantity: string;
  readonly status: FulfillmentStatus;
  readonly supplyStatus: 'unreserved' | 'partially_reserved' | 'ready_to_deliver' | 'fulfilled';
  readonly deliveryStatus: 'not_delivered' | 'partially_delivered' | 'fully_delivered';
}

export interface SalesOrderFulfillment {
  readonly orderRef: string;
  readonly orderStatus: string;
  readonly fulfillmentStatus: FulfillmentStatus;
  readonly supplyStatus: 'unreserved' | 'partially_reserved' | 'ready_to_deliver' | 'fulfilled';
  readonly deliveryStatus: 'not_delivered' | 'partially_delivered' | 'fully_delivered';
  readonly lineCount: number;
  readonly fullyDeliveredLineCount: number;
  readonly lines: readonly SalesOrderFulfillmentLine[];
  readonly reservations: readonly {
    reservationId: string;
    lineId: string;
    quantity: string;
    activeQuantity: string;
    deliveredQuantity: string;
    status: string;
    warehouse: { readonly ref: string; readonly name: string };
    location: { readonly ref: string; readonly name: string };
    createdAt: string;
  }[];
  readonly movements: readonly {
    movementId: string;
    movementNumber: string;
    businessDate: string;
    reservationId: string;
    lineId: string;
    quantity: string;
  }[];
  readonly financeHandoffs: readonly {
    movementId: string;
    status: 'cost_inventory_draft_created' | 'awaiting_cost_inventory_draft';
    label: string;
    journalId: string | null;
    journalNumber: string | null;
    journalStatus: string | null;
  }[];
}

/** ACTIVE_API keys the create/action forms post against — kept close to the
 * label strings so a raw enum value never needs to leak into a component. */
export const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'جدید' },
  { value: 'contacted', label: 'تماس گرفته‌شده' },
  { value: 'qualified', label: 'واجد شرایط' },
  { value: 'converted', label: 'تبدیل‌شده' },
  { value: 'disqualified', label: 'رد شده' },
] as const;

export const ACTIVITY_TYPE_OPTIONS = [
  { value: 'call', label: 'تماس' },
  { value: 'meeting', label: 'جلسه' },
  { value: 'email', label: 'ایمیل' },
  { value: 'task', label: 'کار' },
  { value: 'note', label: 'یادداشت' },
] as const;

/** Real target types the notes endpoint accepts (packages/modules/crm-sales's
 * NoteTargetType). No 'party'/'customer' target exists yet. */
export type NoteTargetType = 'lead' | 'opportunity' | 'activity';

export interface Note {
  readonly ref: string;
  readonly body: string;
  readonly targetType: NoteTargetType;
  readonly targetRef: string;
  readonly createdAt: string;
}

export interface NoteListResponse {
  readonly notes: readonly Note[];
}

/** `GET .../sales/quotes/:ref/flow` — where a pre-invoice went (order → delivery → invoice → finance). */
export interface QuoteFlowOrder {
  readonly ref: string;
  readonly status: SalesOrder['status'];
  readonly statusLabel: string;
  readonly totalRial: string;
  readonly fulfillment: {
    readonly status: FulfillmentStatus;
    readonly statusLabel: string;
    readonly deliveryStatus: 'not_delivered' | 'partially_delivered' | 'fully_delivered';
  };
}

export interface QuoteFlowInvoice {
  readonly invoiceId: string;
  readonly orderRef: string;
  readonly officialNumber: string | null;
  readonly status: SalesInvoiceStatus;
  readonly businessDate: string;
  readonly dueDate: string;
  readonly totalAmountRial: string;
  readonly vatAmountRial: string;
  readonly openBalanceRial: string;
  readonly receivableStatus: string;
  readonly revenueJournalId: string;
  readonly arOpenItemId: string;
}

export interface QuoteFlow {
  readonly quote: {
    readonly ref: string;
    readonly status: QuoteStatus;
    readonly statusLabel: string;
    readonly totalRial: string;
  };
  readonly orders:
    | { readonly status: 'ready'; readonly items: readonly QuoteFlowOrder[] }
    | { readonly status: 'forbidden'; readonly items: readonly [] };
  readonly invoices:
    | { readonly status: 'ready'; readonly items: readonly QuoteFlowInvoice[] }
    | { readonly status: 'forbidden'; readonly items: readonly [] };
}

/** `GET .../sales/orders/:ref/invoice-draft` — delivered, still-uninvoiced quantities. */
export interface InvoiceDraftAllocation {
  readonly movementId: string;
  readonly movementNumber: string;
  readonly deliveredDate: string;
  readonly deliveredQuantity: string;
  readonly invoicedQuantity: string;
  readonly availableQuantity: string;
}

export interface InvoiceDraftLine {
  readonly orderLineId: string;
  readonly rowNumber: number;
  readonly productRef: string;
  readonly productName: string;
  readonly description: string;
  readonly uomRef: string;
  readonly orderedQuantity: string;
  readonly unitPriceRial: string;
  readonly availableQuantity: string;
  readonly allocations: readonly InvoiceDraftAllocation[];
}

export interface InvoiceDraft {
  readonly orderRef: string;
  readonly partyRef: string | null;
  readonly partyName: string | null;
  readonly lines: readonly InvoiceDraftLine[];
  readonly invoiceable: boolean;
  /** Finance-owned destination of the invoice (read-only for the seller). */
  readonly postingAccounts: SalesPostingSettings;
}
